import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useTheme } from '@mui/material/styles';
import Plot from 'react-plotly.js';
import BaseWidget from './base';
import { DashboardWidget, ChartWidgetConfig, DataSourceItem } from '../../../types/dashboard.ui';
import { dashboardAPI } from '../../../api/dashboard';

interface ChartWidgetProps {
  widget: DashboardWidget;
  editMode?: boolean;
  onEdit?: (widget: DashboardWidget) => void;
  onDelete?: (widget: DashboardWidget) => void;
}

interface ChartDataPoint {
  time: Date;
  value: number;
}

interface ChartSeries {
  paramId: string;
  paramName: string;
  paramUnit?: string;
  data: ChartDataPoint[];
  color?: string;
}

const COLORS = [
  '#1976d2', // blue
  '#dc004e', // red
  '#388e3c', // green
  '#f57c00', // orange
  '#7b1fa2', // purple
  '#0097a7', // cyan
  '#5d4037', // brown
  '#455a64', // blue-grey
];

const ChartWidget: React.FC<ChartWidgetProps> = ({ widget, editMode, onEdit, onDelete }: ChartWidgetProps) => {
  const translate = useTranslate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  const [chartData, setChartData] = useState<ChartSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const config = useMemo(() => 
    (typeof widget.config === 'string' ? JSON.parse(widget.config) : widget.config) as ChartWidgetConfig || {}, 
    [widget.config]
  );

  // Извлекаем data_source из виджета (может быть строкой или объектом)
  const widgetDataSource = useMemo(() => {
    if (typeof widget.data_source === 'string') {
      try {
        return JSON.parse(widget.data_source);
      } catch {
        return null;
      }
    }
    return widget.data_source;
  }, [widget.data_source]);

  // Извлекаем источники данных - сначала из widget.data_source, затем из config.dataSource
  const dataSources = useMemo<DataSourceItem[]>(() => {
    // Сначала проверяем widget.data_source.items
    if (widgetDataSource?.items && Array.isArray(widgetDataSource.items)) {
      return widgetDataSource.items;
    }
    // Затем проверяем config.dataSource.items
    if (config.dataSource?.items && Array.isArray(config.dataSource.items)) {
      return config.dataSource.items;
    }
    return [];
  }, [widgetDataSource, config.dataSource]);

  const chartType = config.chartType || config.chart_type || 'line';

  const loadChartData = useCallback(() => {
    if (!dataSources || dataSources.length === 0) {
      setError(translate('resources.dashboards.widgets.chart.no_data_source'));
      setLoading(false);
      return;
    }

    // Проверяем наличие device_id и archive_type
    const firstSource = dataSources[0];
    
    if (!firstSource.deviceId || !firstSource.archiveTypeId) {
      setError(translate('resources.dashboards.widgets.chart.no_data_source'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    // Определяем временной диапазон
    const endTime = new Date();
    const startTime = config.timeRange?.start 
      ? new Date(config.timeRange.start)
      : new Date(endTime.getTime() - 24 * 60 * 60 * 1000); // По умолчанию 24 часа

    // Группируем параметры по устройству и типу архива
    // Для простоты пока берем первый источник данных
    const deviceId = firstSource.deviceId;
    const archiveType = firstSource.archiveTypeId; // Предполагаем, что это varname типа архива
    const paramIds = dataSources.map(ds => ds.paramId);

    dashboardAPI.getHistoricalData(
      deviceId,
      archiveType,
      paramIds,
      startTime.toISOString(),
      endTime.toISOString()
    )
      .then((data) => {
        if (data && Array.isArray(data)) {
          const series: ChartSeries[] = data.map((seriesData, index) => {
            const dataSource = dataSources.find(ds => ds.paramId === seriesData.param_id);
            
            const points: ChartDataPoint[] = (seriesData.values || []).map((v: any) => ({
              time: new Date(v.time),
              value: parseFloat(String(v.value)) || 0
            }));

            return {
              paramId: seriesData.param_id,
              paramName: seriesData.param_name || dataSource?.paramName || dataSource?.paramVarname || seriesData.param_id,
              paramUnit: seriesData.unit || dataSource?.paramUnit,
              data: points,
              color: COLORS[index % COLORS.length]
            };
          });

          setChartData(series);
        } else {
          setChartData([]);
        }
      })
      .catch((err) => {
        console.error('Error loading chart data:', err);
        setError(translate('resources.dashboards.widgets.chart.load_error'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataSources, config.timeRange, translate]);

  useEffect(() => {
    loadChartData();

    // Auto refresh if configured
    let interval: NodeJS.Timeout;
    const refreshInterval = config.refreshInterval || 60000; // По умолчанию 1 минута
    if (refreshInterval > 0) {
      interval = setInterval(loadChartData, refreshInterval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loadChartData, config.refreshInterval]);

  // Построение данных для Plotly
  const plotlyData = useMemo(() => {
    return chartData.map((series) => {
      const baseTrace: any = {
        x: series.data.map(d => d.time),
        y: series.data.map(d => d.value),
        name: series.paramUnit ? `${series.paramName} (${series.paramUnit})` : series.paramName,
        marker: { color: series.color },
        line: { color: series.color },
      };

      switch (chartType) {
        case 'bar':
          return { ...baseTrace, type: 'bar' };
        case 'area':
          return { ...baseTrace, type: 'scatter', mode: 'lines', fill: 'tozeroy' };
        case 'scatter':
          return { ...baseTrace, type: 'scatter', mode: 'markers' };
        case 'line':
        default:
          return { ...baseTrace, type: 'scatter', mode: 'lines+markers' };
      }
    });
  }, [chartData, chartType]);

  // Настройки layout для Plotly
  const plotlyLayout = useMemo(() => {
    const textColor = isDark ? '#ffffff' : '#000000';
    const gridColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
    const bgColor = isDark ? '#1e1e1e' : '#ffffff';

    return {
      autosize: true,
      margin: { t: 30, b: 50, l: 50, r: 20 },
      xaxis: {
        type: 'date',
        tickfont: { color: textColor, size: 10 },
        gridcolor: gridColor,
        title: config.xAxis?.title || '',
      },
      yaxis: {
        tickfont: { color: textColor, size: 10 },
        gridcolor: gridColor,
        title: config.yAxis?.title || '',
      },
      legend: {
        orientation: 'h' as const,
        x: 0.5,
        y: 1.05,
        xanchor: 'center' as const,
        yanchor: 'bottom' as const,
        font: { color: textColor, size: 10 },
      },
      paper_bgcolor: bgColor,
      plot_bgcolor: bgColor,
      dragmode: 'pan' as const,
    };
  }, [isDark, config.xAxis, config.yAxis]);

  const plotlyConfig = useMemo(() => ({
    displaylogo: false,
    responsive: true,
    modeBarButtonsToRemove: ['select2d', 'lasso2d'] as any[],
    displayModeBar: true,
  }), []);

  const dataSourceCount = dataSources.length;

  return (
    <BaseWidget
      widget={widget}
      editMode={editMode}
      onEdit={() => onEdit?.(widget)}
      onDelete={() => onDelete?.(widget)}
      onRefresh={loadChartData}
      loading={loading}
      error={error}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Информация о типе и источниках - только в режиме редактирования */}
        {editMode && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, px: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.dashboards.widgets.chart.type')}: {chartType}
            </Typography>
            {dataSourceCount > 0 && (
              <Typography variant="caption" color="primary">
                {translate('resources.dashboards.widgets.chart.data_sources', { count: dataSourceCount })}
              </Typography>
            )}
          </Box>
        )}
        
        {/* График */}
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {loading && chartData.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%'
            }}>
              <CircularProgress size={40} />
            </Box>
          ) : chartData.length === 0 && !error ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              flexDirection: 'column'
            }}>
              <Typography variant="body2" color="text.secondary">
                {translate('resources.dashboards.widgets.chart.no_data')}
              </Typography>
              {dataSources.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  {translate('resources.dashboards.widgets.chart.configure_data_source')}
                </Typography>
              )}
            </Box>
          ) : (
            <Plot
              data={plotlyData}
              layout={plotlyLayout}
              config={plotlyConfig}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler={true}
            />
          )}
        </Box>
      </Box>
    </BaseWidget>
  );
};

export default ChartWidget;